import React, { useState, useEffect } from 'react';
import { FolderGit2, Github, Loader2, Unplug } from 'lucide-react';
import { getGitHubApp, getGitHubAppConnectUrls, setGitHubAppAllowed, disconnectGitHubApp } from '../../api';

// The organization's GitHub App: connect it once, then tick the repositories agents may
// use through it. Shown on the Agents → Repositories tab (and, until it goes, on the old
// LStack Cloud Agent page). Nothing is rendered when the App is not configured here.
export default function GitHubAppCard({ orgId, notice }) {
  const [app, setApp]           = useState(null);   // null = not loaded, or not available here
  const [selected, setSelected] = useState([]);
  const [busy, setBusy]         = useState(null);
  const [error, setError]       = useState('');
  const [query, setQuery]       = useState('');   // filters the repo list; ticks outside it are kept

  const show = (data) => { setApp(data); setSelected(data.allowed || []); };

  useEffect(() => {
    let live = true;   // a switch of org while this loads must not show the old org's
    setApp(null);
    if (orgId) {
      getGitHubApp()
        .then(({ data }) => { if (live) { setApp(data); setSelected(data.allowed || []); } })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [orgId]);

  if (!orgId || !app?.configured) return null;
  const connected = app.accounts.length > 0;

  const connect = async () => {
    setError('');
    try {
      const { data } = await getGitHubAppConnectUrls();
      localStorage.setItem('github_setup_return', window.location.pathname);
      localStorage.setItem('github_install_url', data.install_url);
      window.location.assign(data.authorize_url);
    } catch (e) { setError(e.response?.data?.detail || "Couldn't reach GitHub"); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect the GitHub App from this organization? Agents stop using it at once. It stays installed on GitHub, and your ticked repositories are kept for when you reconnect.')) return;
    setBusy('drop'); setError('');
    try { show((await disconnectGitHubApp()).data); }
    catch (e) { setError(e.response?.data?.detail || "That didn't work"); }
    finally { setBusy(null); }
  };

  // Ticking a repo saves at once. Optimistic: the box flips, then persists; a failure
  // reverts it. Serialized via `busy`: each save sends the FULL list, so two saves from
  // stale snapshots could clobber each other.
  const toggle = async (repo) => {
    if (busy) return;
    const prev = selected;
    const next = prev.includes(repo) ? prev.filter(r => r !== repo) : [...prev, repo];
    setSelected(next); setBusy('repos'); setError('');
    try { show((await setGitHubAppAllowed(next)).data); }
    catch (e) { setSelected(prev); setError(e.response?.data?.detail || "Couldn't update repositories"); }
    finally { setBusy(null); }
  };

  return (
    <>
      <div className="rounded-2xl border border-panel-border bg-panel p-5">
        <div className="flex items-center gap-2 mb-1 text-fg font-semibold text-sm">
          <Github className="w-4 h-4 text-fg-muted" />GitHub App
          {connected && (
            <span className="ml-auto text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full border border-green-400/20 font-normal">
              Connected
            </span>
          )}
        </div>
        <p className="text-xs text-fg-muted mb-3">
          {connected
            ? 'Agents clone, push and open pull requests with a short-lived token for one ticked repository at a time.'
            : 'Connect your GitHub organization once. Agents clone, push and open pull requests with a short-lived token for one repository at a time — no personal token needed.'}
        </p>
        {/* The sign-in's "connected" line only while it is still true (not after Disconnect). */}
        {notice && connected && <p className="text-xs text-green-400 mb-2">{notice}</p>}
        {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

        {!app.can_manage ? (
          <div className="text-xs text-fg-muted">
            Only an organization owner or admin connects GitHub. Repositories agents may use here:
            {app.allowed.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {app.allowed.map(r => (
                  <span key={r} className="text-[11px] text-fg bg-fg/5 border border-panel-border px-2 py-0.5 rounded-full font-mono">{r}</span>
                ))}
              </div>
            ) : <span> none yet.</span>}
          </div>
        ) : connected ? (
          <>
            <div className="flex flex-wrap gap-1.5">
              {app.accounts.map(a => (
                <span key={a} className="text-[11px] text-fg bg-fg/5 border border-panel-border px-2 py-0.5 rounded-full">{a}</span>
              ))}
            </div>
            {/* Linked before the repos each person can see were kept: none are offered until then. */}
            {app.needs_reconnect && (
              <>
                <p className="text-xs text-orange mt-3 mb-2">Connect again to refresh which repositories you can see.</p>
                <button onClick={connect} disabled={busy !== null}
                  className="px-4 py-2 rounded-lg bg-orange hover:bg-orange-hover text-white text-sm font-semibold
                    whitespace-nowrap transition-colors disabled:opacity-50 flex items-center gap-2">
                  <Github className="w-4 h-4" />Connect GitHub App
                </button>
              </>
            )}
            <button onClick={disconnect} disabled={busy !== null}
              className="mt-3 px-3 py-1.5 rounded-lg border border-red-400/30 bg-red-400/10 text-red-400 hover:bg-red-400/20
                hover:text-red-300 text-xs font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5">
              <Unplug className="w-3.5 h-3.5" />{busy === 'drop' ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        ) : (
          // The page's primary button style (as Save): the one thing to do here.
          <button onClick={connect} disabled={busy !== null}
            className="px-4 py-2 rounded-lg bg-orange hover:bg-orange-hover text-white text-sm font-semibold
              whitespace-nowrap transition-colors disabled:opacity-50 flex items-center gap-2">
            <Github className="w-4 h-4" />Connect GitHub App
          </button>
        )}
      </div>

      {/* The App's repositories: the same picker as the token's, ticked per organization. */}
      {connected && app.can_manage && (
        <div className="rounded-2xl border border-panel-border bg-panel p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-fg font-semibold text-sm">
              <FolderGit2 className="w-4 h-4 text-fg-muted" />Allowed repositories
              {busy === 'repos' && <Loader2 className="w-3.5 h-3.5 animate-spin text-fg-muted" />}
            </div>
            {selected.length > 0 && (
              <span className="text-xs bg-orange/10 text-orange border border-orange/20 px-2 py-0.5 rounded-full font-semibold">
                {selected.length} selected
              </span>
            )}
          </div>
          <p className="text-xs text-fg-muted mb-1">Agents can only use the repositories you tick. None until picked.</p>
          {app.repos.length > 0 && (
            <input
              type="search" value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={`Search ${app.repos.length} repositories`}
              aria-label="Search repositories"
              className="w-full mt-2 text-sm bg-surface border border-field-border rounded-lg px-3 py-2 text-fg
                focus:border-orange outline-none"
            />
          )}
          {app.repos.length === 0 ? (
            <p className="text-xs text-fg-muted py-2">The App can't see any repositories yet.</p>
          ) : !app.repos.some(r => r.repo.toLowerCase().includes(query.trim().toLowerCase())) ? (
            <p className="text-xs text-fg-muted py-2">No repository matches "{query.trim()}".</p>
          ) : (
            <div className="max-h-64 overflow-y-auto divide-y divide-panel-border/50 -mx-1 mt-2">
              {app.repos.filter(r => r.repo.toLowerCase().includes(query.trim().toLowerCase())).map(r => (
                <label key={r.repo}
                  className="flex items-center gap-3 py-2.5 px-1 hover:bg-fg/5 rounded-lg cursor-pointer">
                  <input type="checkbox"
                    checked={selected.includes(r.repo)}
                    disabled={busy !== null}
                    onChange={() => toggle(r.repo)}
                    className="accent-orange w-4 h-4 shrink-0"
                  />
                  <span className="text-sm text-fg truncate flex-1">{r.repo}</span>
                  {r.account && <span className="text-[11px] text-fg-muted shrink-0">{r.account}</span>}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {connected && (
        <p className="text-xs text-fg-muted px-1">Repos not ticked here use the personal token below.</p>
      )}
    </>
  );
}
